import {Injectable, signal} from '@angular/core';

/**
 * Zoom der Grafik-Karten, unabhängig vom Browser-Zoom.
 *
 * Die Kartenbreite kommt aus dem Server-Template als
 * {@code calc(var(--karten-zoom, 39%) - 12px)}. Dieser Service setzt die CSS-Variable
 * global am Wurzelelement – dadurch wirkt eine Änderung sofort auf alle Karten in
 * allen Reitern, ohne dass Komponenten davon wissen müssen.
 *
 * Weil unterhalb der Karte alles relativ definiert ist (Prozent des Referenzraums,
 * cqw für die Schrift, em für Umschalter und Kreise), skalieren Boxen, Beschriftungen
 * und Windpfeile automatisch mit.
 *
 * Der Wert liegt in einem Signal, weil ihn mehrere Regler gleichzeitig anzeigen: einer
 * in der Kopfleiste, je einer neben jeder Reihenüberschrift. Ein Klick in einem von
 * ihnen ändert geteilten Zustand, den die anderen lesen – über das Signal markiert
 * Angular alle betroffenen Ansichten selbst, unabhängig von deren
 * Change-Detection-Strategie. Ohne das hinge es daran, ob die Prüf-Traversierung die
 * Geschwister-Ansicht erreicht.
 */
@Injectable({
  providedIn: 'root'
})
export class KartenZoomService {

  private static readonly VARIABLE = '--karten-zoom';
  private static readonly SPEICHER_SCHLUESSEL = 'dmx4.kartenZoom';
  /** Entspricht dem Standardwert im Server-Template. */
  static readonly BASIS = 39;
  private static readonly MIN = 15;
  private static readonly MAX = 100;
  private static readonly SCHRITT = 4;

  private readonly faktor = signal(KartenZoomService.BASIS);

  constructor() {
    const gespeichert = Number(localStorage.getItem(KartenZoomService.SPEICHER_SCHLUESSEL));
    if (!isNaN(gespeichert) && gespeichert > 0) {
      this.faktor.set(this.begrenzen(gespeichert));
    }
    this.anwenden();
  }

  /** Aktuelle Kartenbreite in Prozent der Zeilenbreite. */
  get wert(): number {
    return this.faktor();
  }

  /** Ist der Zoom bereits am Anschlag? Dient dem Deaktivieren der Buttons. */
  get amMaximum(): boolean {
    return this.faktor() >= KartenZoomService.MAX;
  }

  get amMinimum(): boolean {
    return this.faktor() <= KartenZoomService.MIN;
  }

  groesser(): void {
    this.setze(this.faktor() + KartenZoomService.SCHRITT);
  }

  kleiner(): void {
    this.setze(this.faktor() - KartenZoomService.SCHRITT);
  }

  /** Zurück auf die Vorgabe des Templates. */
  zuruecksetzen(): void {
    this.setze(KartenZoomService.BASIS);
  }

  private setze(wert: number): void {
    this.faktor.set(this.begrenzen(wert));
    this.anwenden();
    localStorage.setItem(KartenZoomService.SPEICHER_SCHLUESSEL, String(this.faktor()));
  }

  private begrenzen(wert: number): number {
    return Math.min(Math.max(Math.round(wert), KartenZoomService.MIN), KartenZoomService.MAX);
  }

  private anwenden(): void {
    document.documentElement.style.setProperty(KartenZoomService.VARIABLE, this.faktor() + '%');
  }
}
